import {
  Button,
  CircularProgress,
  Typography,
  Box,
  Alert,
} from '@mui/material';
import React, { useState } from 'react';
import { syncJiraIssuesForProject } from '../../modules/associations';
import Modal from '../elements/Modal';
import { useNotification } from '../../hooks/NotificationContext';

const SyncJiraIssuesModal = ({ currentProject, setShowSyncModal, showSyncModal }) => {
  const [isSyncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState(null);
  const {addNotification} = useNotification();

  const handleSyncJiraIssues = async () => {
    setSyncing(true);
    try {
      const result = await syncJiraIssuesForProject(currentProject?.id);
      setSyncResult(result);
    } catch (error) {
      console.error('Failed to sync Jira issues:', error);
      addNotification('danger', 'Failed to sync Jira issues. Please try again.');
      handleClose();
    } finally {
      setSyncing(false);
    }
  };

  const handleClose = () => {
    setShowSyncModal(false);
    setTimeout(() => setSyncResult(null), 100);
  };

  return (
    <Modal
      open={showSyncModal}
      width="sm"
      handleClose={isSyncing ? undefined : handleClose}
      modalTitle="Sync Jira Issues"
      disableEscapeKeyDown
      action={() => (
        <>
          {syncResult ? (
            <Button variant="contained" color="primary" onClick={handleClose}>
              Close
            </Button>
          ) : (
            <>
              <Button onClick={handleClose} disabled={isSyncing}>
                Cancel
              </Button>
              <Button
                variant="contained"
                color="primary"
                onClick={handleSyncJiraIssues}
                disabled={isSyncing}>
                {isSyncing ? 'Syncing...' : 'Sync'}
              </Button>
            </>
          )}
        </>
      )}>
      {isSyncing ? (
        <Box textAlign="center">
          <CircularProgress />
          <Typography variant="body1" sx={{ mt: 2 }}>
            Syncing Jira issues... Please do not refresh or close the window
            until the process is complete.
          </Typography>
        </Box>
      ) : syncResult ? (
        <Box>
          <Alert severity="success" sx={{ mb: 2 }}>
            {syncResult.updated_count > 0
              ? `Successfully updated ${syncResult.updated_count} issue(s).`
              : 'All linked issues are up to date.'}
          </Alert>
          {syncResult.updated_ids?.length > 0 && (
            <>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Updated Issue Keys:
              </Typography>
              <Typography variant="body2">
                {syncResult.updated_ids.join(', ')}
              </Typography>
            </>
          )}
        </Box>
      ) : (
        <>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Sync linked Jira issues for this project to ensure their information
            in GAINSS is up to date with the latest data in Jira. This is
            recommended if issues were migrated to a different Jira project
            after initial linking.
          </Typography>
          <Typography variant="body1" sx={{ mb: 2, color: '#f44336' }}>
            Please ensure that the Jira project to which the issues were
            migrated is linked to the current GAINSS project.
          </Typography>
        </>
      )}
    </Modal>
  );
};

export default SyncJiraIssuesModal;
